/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import * as biojs from 'BioJS';
import * as Util from 'biojs/Util';
import {Genome} from 'constants/GenomeConstants';
import {SequenceSummary} from 'constants/SequenceConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import ChromosomeImportForm from 'import-sequence/ChromosomeImportForm';
import SequenceImportInput from 'import-sequence/SequenceImportInput';
import notifications from 'notifications';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import {Optional} from 'util/Types';

import ImportGeneInput from './ImportGeneInput';

const {sanitize, formatBaseCount} = Util;

interface RawBasesInputState {
  bases: string;
  folderId: Optional<string>;
  name: string;
}

class RawBasesInput extends React.Component<{}, RawBasesInputState> {
  override state: RawBasesInputState = {
    bases: '',
    folderId: FolderDefaults.get(FolderDefaultsKey.INVENTORY),
    name: 'Untitled Sequence',
  };

  _handleChangeFolderId = (e: any): void => {
    this.setState({folderId: e.target.value});
  };

  getGenome = (): null => null;

  updateBases = (e: any): void => {
    this.setState({bases: sanitize(e.target.value, Util.NucleotideType.DNA)});
  };

  createSequence = (): null | JQueryPromise<string> => {
    if (!(this.state.bases.length > 0 && this.state.folderId && this.state.name.trim().length > 0)) {
      notifications.error('Bases, folder, and name must all be specified.');
      return null;
    }

    const sequence = new biojs.Sequence({
      bases: this.state.bases,
      folder: this.state.folderId,
      name: this.state.name,
    });
    return sequence.save().then(() => sequence.id);
  };

  override render(): JSX.Element {
    return (
      <div>
        <FormGroup>
          <Label>Sequence name</Label>
          <input
            type="text"
            value={this.state.name}
            onChange={(e) => {
              this.setState({name: e.target.value});
            }}
          />
        </FormGroup>
        <FormGroup>
          <Label>{DEFAULT_PROJECT_FOLDER_LABEL_TEXT}</Label>
          <Row>
            <Col sm={6}>
              <FolderSelector
                showInventory={true}
                value={this.state.folderId}
                onChange={this._handleChangeFolderId}
                unsetOnFetchFail={true}
              />
            </Col>
          </Row>
        </FormGroup>
        <FormGroup>
          <Label className="u-input-line-height">Bases</Label>
          <textarea
            className="fixed-width-textarea form-control"
            style={{width: '100%'}}
            rows={4}
            value={this.state.bases}
            onChange={this.updateBases}
          />
        </FormGroup>
      </div>
    );
  }
}

interface FileInputState {
  folderId: string | null;
  importError: string | null;
  sequence: null | SequenceSummary;
  sequenceName: string;
}

class FileInput extends React.Component<{}, FileInputState> {
  override state: FileInputState = {
    folderId: null,
    importError: null,
    sequence: null,
    sequenceName: '',
  };

  _handleChangeFolderId = (e: any): void => {
    this.setState({folderId: e.target.value});
  };

  getGenome = (): null => null;

  createSequence = (): null | Promise<string> => {
    if (this.state.sequence == null) {
      notifications.error('Upload a file to continue.');
      return null;
    }
    if (!(this.state.sequenceName.trim().length > 0 && this.state.folderId != null)) {
      notifications.error('Name and folder must be specified.');
      return null;
    }
    const data = {name: this.state.sequenceName, folder: this.state.folderId};
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, this.state.folderId);
    return FileBrowserDAO.updateApiIdentified(this.state.sequence.id, data, {}).then(
      () => this.state.sequence!.id
    );
  };

  override render(): JSX.Element {
    return (
      <div>
        {this.state.sequence && (
          <div>
            <FormGroup>
              <Label>Sequence name</Label>
              <input
                type="text"
                value={this.state.sequenceName}
                onChange={(e) => {
                  this.setState({sequenceName: e.target.value});
                }}
              />
              <div className="faded u-tiny-spacing-top">
                <small>
                  {(() => {
                    const {circular, length} = this.state.sequence;
                    const topology = circular ? 'circular' : 'linear';
                    return `${formatBaseCount(length)} \u00B7 ${topology}`;
                  })()}
                </small>
              </div>
            </FormGroup>
            <FormGroup>
              <Label>{DEFAULT_PROJECT_FOLDER_LABEL_TEXT}</Label>
              <Row>
                <Col sm={6}>
                  <FolderSelector
                    showInventory={true}
                    value={this.state.folderId}
                    onChange={this._handleChangeFolderId}
                    unsetOnFetchFail={true}
                  />
                </Col>
              </Row>
            </FormGroup>
            <hr key="hr" />
          </div>
        )}
        <FormGroup>
          <SequenceImportInput
            folderId={FolderDefaults.get(FolderDefaultsKey.INVENTORY)!}
            importErrorLink={{
              value: this.state.importError,
              requestChange: (newValue: string | null) => {
                this.setState({importError: newValue});
              },
            }}
            multi={false}
            updateSequences={([sequence]) => {
              this.setState({
                sequence,
                sequenceName: sequence.name,
                folderId: sequence.folder.api_identifier,
              });
            }}
          />
        </FormGroup>
      </div>
    );
  }
}

class ChromosomeInput extends React.Component {
  importFormRef = React.createRef<ChromosomeImportForm>();

  getGenome = (): Genome | undefined => this.importFormRef.current!.getGenome();

  createSequence = (): Promise<any> | null => {
    const sequenceDeferred = this.importFormRef.current!.createSequence();
    if (!sequenceDeferred) {
      return null;
    }
    return sequenceDeferred.then(({id}: any) => id);
  };

  override render(): JSX.Element {
    return (
      <ChromosomeImportForm
        ref={this.importFormRef}
        initialFolderId={FolderDefaults.get(FolderDefaultsKey.INVENTORY)!}
      />
    );
  }
}

const CRISPR_TYPE_GENOME_MOD = 'genome-mod';
const CRISPR_TYPE_CRISPR_IA = 'crispr-ia';

const CREATE_TYPE_RAW_BASES = 'raw-bases';
const CREATE_TYPE_FILE = 'file';
const CREATE_TYPE_CHROMOSOME = 'chromosome';
const CREATE_TYPE_GENE = 'gene';

interface CreateSequenceStepProps {
  onChangeIsCrisprIa?: (...args: Array<any>) => any;
}

interface CreateSequenceStepState {
  createType?: string;
  crisprType: string;
}

class CreateSequenceStep extends React.Component<CreateSequenceStepProps, CreateSequenceStepState> {
  override state: CreateSequenceStepState = {
    createType: CREATE_TYPE_GENE,
    crisprType: CRISPR_TYPE_GENOME_MOD,
  };

  sequenceInputRef = React.createRef<ImportGeneInput | RawBasesInput | FileInput | ChromosomeInput>();

  getGenome = (): Optional<Genome> => this.sequenceInputRef.current!.getGenome();

  handleChangeCreateType = (e: any): void => {
    this.setState({createType: e.target.value});
  };

  handleChangeCrisprType = (e: any): void => {
    const state: {
      crisprType: string;
      createType?: string;
    } = {
      crisprType: e.target.value,
    };
    if (state.crisprType === CRISPR_TYPE_CRISPR_IA) {
      state.createType = CREATE_TYPE_GENE;
    }
    this.props.onChangeIsCrisprIa!(state.crisprType === CRISPR_TYPE_CRISPR_IA);
    this.setState(state);
  };

  updateCreateType = (e: any): void => {
    this.setState({createType: e.target.value});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/explicit-module-boundary-types
  createSequence = () => this.sequenceInputRef.current!.createSequence();

  override render(): JSX.Element {
    return (
      <div>
        {BenchlingConfig.ENABLE_CRISPR_IA && this.props.onChangeIsCrisprIa && (
          <div>
            <FormGroup>
              <label className="u-spacing-right">Experiment Type</label>
              <span>
                <label>
                  <input
                    type="radio"
                    checked={this.state.crisprType === CRISPR_TYPE_GENOME_MOD}
                    value={CRISPR_TYPE_GENOME_MOD}
                    onChange={this.handleChangeCrisprType}
                  />
                  <span className="u-tiny-spacing-left">Genome modification</span>
                </label>
              </span>
              <span className="u-spacing-left">
                <label>
                  <input
                    type="radio"
                    checked={this.state.crisprType === CRISPR_TYPE_CRISPR_IA}
                    value={CRISPR_TYPE_CRISPR_IA}
                    onChange={this.handleChangeCrisprType}
                  />
                  <span className="u-tiny-spacing-left">CRISPRi, CRISPRa</span>
                </label>
              </span>
            </FormGroup>
          </div>
        )}
        <div>
          <FormGroup className="crispr-importSelector">
            <label>Import from</label>
            <select
              value={this.state.createType}
              onChange={this.handleChangeCreateType}
              disabled={this.state.crisprType === CRISPR_TYPE_CRISPR_IA}
            >
              <option value={CREATE_TYPE_GENE}>Gene ID or Name</option>
              <option value={CREATE_TYPE_CHROMOSOME}>Chromosomal Coordinates</option>
              <option value={CREATE_TYPE_FILE}>Sequence File (Genbank, FASTA, etc.)</option>
              <option value={CREATE_TYPE_RAW_BASES}>Raw Bases</option>
            </select>
          </FormGroup>
          {(() => {
            if (this.state.crisprType === CRISPR_TYPE_CRISPR_IA) {
              return (
                <ImportGeneInput
                  ref={this.sequenceInputRef as React.RefObject<ImportGeneInput>}
                  enableAdvancedSettings={false}
                  initialUpstream={800}
                />
              );
            }
            switch (this.state.createType) {
              case CREATE_TYPE_RAW_BASES:
                return <RawBasesInput ref={this.sequenceInputRef as React.RefObject<RawBasesInput>} />;
              case CREATE_TYPE_FILE:
                return <FileInput ref={this.sequenceInputRef as React.RefObject<FileInput>} />;
              case CREATE_TYPE_CHROMOSOME:
                return <ChromosomeInput ref={this.sequenceInputRef as React.RefObject<ChromosomeInput>} />;
              case CREATE_TYPE_GENE:
                return <ImportGeneInput ref={this.sequenceInputRef as React.RefObject<ImportGeneInput>} />;
              default:
                throw new Error();
            }
          })()}
        </div>
      </div>
    );
  }
}

export default CreateSequenceStep;
